<script lang="ts">
import type { ImagePreviewTool } from '@satellite/components/elements/preview/ImagePreviewDialog.vue';
import type { PdfPreviewTool } from '@satellite/components/elements/preview/PdfPreviewDialog.vue';

// Module scope: prop defaults are hoisted out of `setup`, so they cannot read its bindings.
const DEFAULT_IMAGE_TOOLS: ImagePreviewTool[] = ['zoom', 'pan', 'download'];
const DEFAULT_PDF_TOOLS: PdfPreviewTool[] = ['zoom', 'download'];
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import ImagePreviewDialog from '@satellite/components/elements/preview/ImagePreviewDialog.vue';
import PdfPreviewDialog from '@satellite/components/elements/preview/PdfPreviewDialog.vue';
import documentService from '@satellite/services/document-service';
import { canSaveFile, downloadFile } from '@satellite/helpers/downloadFile';
import {
  ATTACHMENT_SCRIM_OPACITY,
  canPreviewAttachment,
  isImageAttachment,
  isPdfAttachment
} from '@satellite/helpers/attachmentUtils';

import type { Maybe, YardTaskAttachment } from '@nova/core';

defineOptions({ name: 'AttachmentPreview' });

const {
  imageTools = DEFAULT_IMAGE_TOOLS,
  pdfTools = DEFAULT_PDF_TOOLS,
  testId = 'attachment'
} = defineProps<{
  /** Which controls the image preview offers. One image at a time, so no navigation. */
  imageTools?: ImagePreviewTool[];
  pdfTools?: PdfPreviewTool[];
  /** Prefix for the dialogs' `data-testid`s, e.g. `<testId>-preview-close`. */
  testId?: string;
}>();

/** The attachment on screen. Setting it opens the matching preview; clearing it closes. */
const attachment = defineModel<Maybe<YardTaskAttachment>>({ default: null });

const emit = defineEmits<{
  /**
   * Nothing can be saved from here at all, so no request was made. Separate from a failed one
   * because an app whose interceptor reports API errors still has to word this itself.
   */
  unsupported: [];
  /** Reading the bytes back failed. */
  'error:download': [error: unknown];
}>();

/**
 * Held past the close on purpose: dropping the attachment as the dialog leaves would empty it
 * mid-transition, collapsing it to its bars on the way out.
 */
const previewedAttachment = ref<Maybe<YardTaskAttachment>>(attachment.value);

// `immediate` so an attachment handed over at mount is opened, or saved, like any other.
watch(
  attachment,
  value => {
    if (!value) {
      return;
    }

    previewedAttachment.value = value;

    if (!canPreviewAttachment(value)) {
      saveAttachment(value);
    }
  },
  { immediate: true }
);

const isImagePreviewOpen = computed({
  get: () => Boolean(attachment.value && isImageAttachment(attachment.value)),
  set: value => {
    if (!value) {
      attachment.value = null;
    }
  }
});

const isPdfPreviewOpen = computed({
  get: () => Boolean(attachment.value && isPdfAttachment(attachment.value)),
  set: value => {
    if (!value) {
      attachment.value = null;
    }
  }
});

/** A single-item list: the preview renders plainly, with no navigation between attachments. */
const images = computed(() =>
  previewedAttachment.value && isImageAttachment(previewedAttachment.value)
    ? [previewedAttachment.value.url]
    : []
);

/**
 * Read through the API rather than from the presigned URL: an `<img>` needs no CORS, but
 * fetching the bytes does, and the bucket has none. Without it the browser can only offer to
 * open the file in a tab, which a standalone PWA cannot come back from.
 */
function loadDocument(): Promise<string> {
  return documentService.getDownloadBlobUrl(previewedAttachment.value?.id ?? '');
}

/**
 * What no dialog here can render is saved instead of opened. Handing it to the browser as a
 * link would leave a standalone PWA on a page it cannot come back from, so the bytes come
 * through the API and `downloadFile` picks the way out that the platform actually has.
 */
async function saveAttachment(target: YardTaskAttachment): Promise<void> {
  // Nothing stays open for it: the save is the whole interaction.
  attachment.value = null;

  /**
   * An installed app with no share sheet has no way to hand a file out, and `downloadFile`
   * has no fallback there. Said now rather than leaving the tap doing nothing at all.
   */
  if (!canSaveFile()) {
    emit('unsupported');

    return;
  }

  let objectUrl = '';

  try {
    objectUrl = await documentService.getDownloadBlobUrl(target.id);
    await downloadFile(objectUrl, target.name);
  } catch (error) {
    emit('error:download', error);
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
</script>

<template>
  <image-preview-dialog
    v-model="isImagePreviewOpen"
    :images="images"
    :file-name="previewedAttachment?.name ?? ''"
    :tools="imageTools"
    :download-source="loadDocument"
    size="fullscreen"
    :scrim-opacity="ATTACHMENT_SCRIM_OPACITY"
    :test-id="`${testId}-preview`" />

  <pdf-preview-dialog
    v-model="isPdfPreviewOpen"
    :src="loadDocument"
    :file-name="previewedAttachment?.name ?? ''"
    :tools="pdfTools"
    size="fullscreen"
    :scrim-opacity="ATTACHMENT_SCRIM_OPACITY"
    :test-id="`${testId}-pdf-preview`">
    <template #error>
      <!-- @slot Shown when the document cannot be read. -->
      <slot name="pdf-error" />
    </template>
  </pdf-preview-dialog>
</template>
